import { extends as _extends } from '../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { useContext, useRef, useState, useEffect } from 'react';
import { I18nContext } from './context.js';
import { defaultLocaleCtx, DEFAULT_NAMESPACE, LOADED, LANG_CHANGED } from './constants.js';
import { getLang, getPluralIndexRu, interpolateKey } from './utils.js';
import { isTransOkay, noop, loadLanguages, loadNamespaces } from './internal-utils.js';
import ru from '../locales/ru.json.js';

var useDefaultLocale = function useDefaultLocale() {
  return defaultLocaleCtx;
};
function useLocale() {
  var defaultLocale = useDefaultLocale();
  var context = useContext(I18nContext);
  var value = context || defaultLocale;
  return value;
}

var useTranslationFacade = function useTranslationFacade() {
  var _useLocale = useLocale(),
      novaInstance = _useLocale.novaInstance,
      lang = _useLocale.lang,
      locale = _useLocale.locale,
      useSuspense = _useLocale.useSuspense,
      novaInitialized = _useLocale.novaInitialized;

  var i18n = novaInstance;
  var tIsOk = useRef(false);

  var _useState = useState(function () {
    if (i18n && novaInitialized && (!!i18n.isInitialized || !useSuspense)) {
      tIsOk.current = true;
      return i18n == null ? void 0 : i18n.getFixedT(null, DEFAULT_NAMESPACE);
    }

    return noop;
  }),
      t = _useState[0],
      update = _useState[1];

  useEffect(function () {
    if (tIsOk.current && i18n) {
      i18n.on(LOADED, function () {
        update(function () {
          return i18n == null ? void 0 : i18n.getFixedT(null, DEFAULT_NAMESPACE);
        });
      });
      i18n.on(LANG_CHANGED, function () {
        update(function () {
          return i18n == null ? void 0 : i18n.getFixedT(null, DEFAULT_NAMESPACE);
        });
      });
      return function () {
        i18n.off(LOADED, function () {
          update(function () {
            return i18n == null ? void 0 : i18n.getFixedT(null, DEFAULT_NAMESPACE);
          });
        });
        i18n.off(LANG_CHANGED, function () {
          update(function () {
            return i18n == null ? void 0 : i18n.getFixedT(null, DEFAULT_NAMESPACE);
          });
        });
      };
    }

    return undefined;
  }, [i18n, locale]);
  useEffect(function () {
    if (i18n && !tIsOk.current) {
      if (i18n != null && i18n.isInitialized) {
        update(function () {
          return i18n == null ? void 0 : i18n.getFixedT(null, DEFAULT_NAMESPACE);
        });
      }

      if (!(i18n != null && i18n.isInitialized) && !useSuspense) {
        loadLanguages(i18n, locale, function (i18nInstance) {
          if (i18nInstance != null && i18nInstance.getFixedT) {
            update(function () {
              return i18nInstance == null ? void 0 : i18nInstance.getFixedT(null, DEFAULT_NAMESPACE);
            });
          }
        });
        loadNamespaces(i18n, DEFAULT_NAMESPACE, function (i18nInstance) {
          if (i18nInstance != null && i18nInstance.getFixedT) {
            update(function () {
              return i18nInstance == null ? void 0 : i18nInstance.getFixedT(null, DEFAULT_NAMESPACE);
            });
          }
        });
      }
    }
  }, [i18n, i18n == null ? void 0 : i18n.isInitialized, locale, useSuspense]);

  if (i18n != null && i18n.isInitialized && isTransOkay(t) || !useSuspense) {
    return {
      t: t,
      i18n: i18n
    };
  }

  if (!i18n) {
    return {
      i18n: i18n,
      t: noop
    };
  }

  throw new Promise(function (resolve) {
    loadLanguages(i18n, lang, function () {
      return resolve(true);
    });
  });
};

function useDirection() {
  var _useLocale2 = useLocale(),
      dir = _useLocale2.dir,
      novaInitialized = _useLocale2.novaInitialized;

  var _useTranslationFacade = useTranslationFacade(),
      t = _useTranslationFacade.t,
      i18n = _useTranslationFacade.i18n;

  if (dir) return dir;

  if (isTransOkay(t) && novaInitialized) {
    return (i18n == null ? void 0 : i18n.dir()) || 'ltr';
  }

  return 'ltr';
}

var getTranslationFallback = function getTranslationFallback(component) {
  return function (key, options) {
    var localeDefault = ru;

    if (!(component in localeDefault)) {
      var _process, _process$env;

      if (((_process = process) == null ? void 0 : (_process$env = _process.env) == null ? void 0 : _process$env.NODE_ENV) === 'development') {
        throw new Error('[nova.i18n] no l10n-able component ');
      }

      return key;
    }

    if (options != null && options.count && Number.isInteger(options == null ? void 0 : options.count)) {
      var tKey = key + "_" + getPluralIndexRu(options.count);
      return interpolateKey(localeDefault[component][tKey], 'count', options.count);
    }

    return localeDefault[component][key];
  };
};

function useDictionary(_ref) {
  var componentName = _ref.componentName;

  var _useLocale3 = useLocale(),
      novaInitialized = _useLocale3.novaInitialized;

  var _useTranslationFacade2 = useTranslationFacade(),
      t = _useTranslationFacade2.t,
      i18n = _useTranslationFacade2.i18n;

  if (!isTransOkay(t) || !novaInitialized || !(i18n != null && i18n.isInitialized)) {
    return getTranslationFallback(componentName);
  }

  return function (key, options) {
    return t(componentName + "." + String(key), _extends({}, options, {
      ns: DEFAULT_NAMESPACE,
      lng: i18n == null ? void 0 : i18n.language
    }));
  };
}
/* NOTE: i18next возвращает каждый раз новую ссылку, закэшируем в глобальном объекте */

var fnsCache = {};
function useCustomDateFns() {
  var _useLocale4 = useLocale(),
      novaInitialized = _useLocale4.novaInitialized;

  var _useTranslationFacade3 = useTranslationFacade(),
      t = _useTranslationFacade3.t,
      i18n = _useTranslationFacade3.i18n;

  if (!isTransOkay(t) || !novaInitialized || !(i18n != null && i18n.isInitialized)) {
    return null;
  }

  var dateFns = t('dateFns', {
    returnObjects: true
  });
  var dateFnsDetails = t('dateFns', {
    returnDetails: true
  });

  if (!dateFns || typeof dateFns === 'string') {
    return null;
  }

  if (dateFnsDetails.usedLng in fnsCache) {
    return fnsCache[dateFnsDetails.usedLng];
  }

  fnsCache[dateFnsDetails.usedLng] = dateFns;
  return fnsCache[dateFnsDetails.usedLng];
}
var useLocaleValues = function useLocaleValues(props) {
  var locale = props.locale,
      dir = props.dir;
  var defaultLocale = useDefaultLocale();
  var localeValue = locale || defaultLocale.locale;
  var dirValue = dir || defaultLocale.dir;
  var lang = getLang(localeValue);
  return {
    dirValue: dirValue,
    lang: lang,
    localeValue: localeValue
  };
};
var useCSRUpdates = function useCSRUpdates(props) {
  var i18nInstance = props.i18nInstance,
      locale = props.locale,
      dictionary = props.dictionary,
      nextState = props.nextState;
  React__default.useEffect(function () {
    if (i18nInstance != null && i18nInstance.isInitialized && locale !== i18nInstance.language && nextState.novaInitialized) {
      // eslint-disable-next-line no-void
      void i18nInstance.changeLanguage(locale);
    }
  }, [locale, i18nInstance, nextState.novaInitialized]);
  React__default.useEffect(function () {
    if (i18nInstance && i18nInstance.isInitialized && dictionary && nextState.novaInitialized) {
      Object.keys(dictionary).forEach(function (key) {
        i18nInstance.addResourceBundle(key, DEFAULT_NAMESPACE, dictionary[key]);
      });
    }
  }, [dictionary, i18nInstance, nextState.novaInitialized]);
};

export { useCSRUpdates, useCustomDateFns, useDefaultLocale, useDictionary, useDirection, useLocale, useLocaleValues };
